import { Card } from "./card";

type PokerHandMatch = "Royal Flush" | "Straight Flush" | "Four of a Kind" | "Full House"| "Flush" | "Straight" | "Three of a Kind" | "Two Pair" | "One Pair";

export type PokerHandResult = {
    match: PokerHandMatch;
    points: number;
    cards: Card[];
    // Bonus for clearing board
    bonus?: number;
};

const POINTS = {
    royalFlush: 100,
    straightFlush: 75,
    fourOfAKind: 50,
    fullHouse: 30,
    flush: 20,
    straight: 15,
    threeOfAKind: 10,
    twoPair: 5,
    onePair: 2
};

export function checkHand(cards: Card[]): PokerHandResult | undefined {
    if (cards.length > 1) {
        // Sort by ordinal value
        cards = cards.slice().sort((a, b) => a.ordinal - b.ordinal);

        const result = checkRoyalFlush(cards) ??
            checkStraightFlush(cards) ??
            checkFourOfAKind(cards) ??
            checkFullHouse(cards) ??
            checkFlush(cards) ??
            checkStraight(cards) ??
            checkThreeOfAKind(cards) ??
            checkTwoPair(cards) ??
            checkOnePair(cards);

        return result;
    }
}

function checkOnePair(cards: Card[]): PokerHandResult | undefined {
    if (cards.length >= 2) {
        for (let i = 0; i < cards.length - 1; i++) {
            let rank = cards[i].ordinal;
            if (cards[i + 1].ordinal === rank) {
                return {
                    match: "One Pair",
                    cards: [cards[i], cards[i + 1]],
                    points: POINTS.onePair
                };
            }
        }
    }
}


function checkTwoPair(cards: Card[]): PokerHandResult | undefined {
    if (cards.length >= 4) {
        let matches = [];
        for (let i = 0; i < cards.length - 1; i++) {
            if (cards[i].ordinal === cards[i + 1].ordinal) {
                matches.push(cards[i], cards[i + 1]);
            }
        }

        if (matches.length === 4) {
            return {
                match: "Two Pair",
                cards: matches,
                points: POINTS.twoPair
            };
        }
    }
}

function checkThreeOfAKind(cards: Card[]): PokerHandResult | undefined {
    if (cards.length >= 3) {
        for (let i = 0; i < cards.length - 2; i++) {
            const ord = cards[i].ordinal;
            if (cards[i + 1].ordinal === ord && cards[i + 2].ordinal === ord) {
                return {
                    match: "Three of a Kind",
                    cards: [cards[i], cards[i + 1], cards[i + 2]],
                    points: POINTS.threeOfAKind
                };
            }
        }
    }
}

function checkStraight(cards: Card[]): PokerHandResult | undefined {
    if (cards.length >= 5) {
        let isStraight = true;
        let ord = cards[0].ordinal;
        for (let i = 1; i < cards.length && isStraight; i++) {
            isStraight = (cards[i].ordinal === ++ord);
        }

        // If we have an ace and a king try the ace at the end
        if (!isStraight && cards[0].ordinal === 1 && cards[4].ordinal === 13) {
            // Move the ace to the end and give it an ordinal of 14
            let altCards = cards.slice(1);
            altCards.push({ suit: cards[0].suit, rank: cards[0].rank, ordinal: 14 });
            return checkStraight(altCards);
        }

        if (isStraight) {
            return {
                match: "Straight",
                cards: cards,
                points: POINTS.straight
            };
        }
    }
}

function checkFlush(cards: Card[]): PokerHandResult | undefined {
    if (cards.length >= 5) {
        let suit = cards[0].suit;
        for (let i = 1; i < cards.length; i++) {
            if (cards[i].suit !== suit) return undefined;
        }

        return {
            match: "Flush",
            cards: cards,
            points: POINTS.flush
        };
    }
}

function checkFullHouse(cards: Card[]): PokerHandResult | undefined {
    if (cards.length >= 5) {
        if (checkThreeOfAKind(cards)) {
            // Check the other two cards
            if ((cards[0].ordinal === cards[1].ordinal) &&
                (cards[3].ordinal === cards[4].ordinal)) {
                return {
                    match: "Full House",
                    cards: cards,
                    points: POINTS.fullHouse
                };
            }
        }
    }
}

function checkFourOfAKind(cards: Card[]): PokerHandResult | undefined {
    if (cards.length >= 4) {
        for (let i = 0; i < 2; i++) {
            let rank = cards[i].ordinal;
            if (cards[i + 1].ordinal === rank &&
                cards[i + 2].ordinal === rank &&
                cards[i + 3].ordinal === rank) {
                return {
                    match: "Four of a Kind",
                    cards: [cards[i], cards[i + 1], cards[i + 2], cards[i + 3]],
                    points: POINTS.fourOfAKind
                };
            }
        }
    }
}

function checkStraightFlush(cards: Card[]): PokerHandResult | undefined {
    if (checkStraight(cards) && checkFlush(cards)) {
        return {
            match: "Straight Flush",
            cards: cards,
            points: POINTS.straightFlush
        };    
    }
}

function checkRoyalFlush(cards: Card[]): PokerHandResult | undefined {
    if (checkStraightFlush(cards) &&
        cards[0].ordinal === 1 &&
        cards[1].ordinal === 10 &&
        cards[2].ordinal === 11 &&
        cards[3].ordinal === 12 &&
        cards[4].ordinal === 13) {
        return {
            match: "Royal Flush",
            cards: cards,
            points: POINTS.royalFlush
        };
    }
}
